'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from '@/components/LanguageSwitcher/LanguageSwitcher';
import { LogoIcon } from '@/components/ui/Icons/Icons';
import { SocialLinks } from '@/components/ui/SocialLinks/SocialLinks';
import { CONFIG } from '@/config';
import { ROUTES } from '@/constants/common';
import { useBelow } from '@/hooks/useBelow';
import { useStaticPages } from '@/hooks/useStaticPages';

import * as S from './Footer.styles';

const { HOME } = ROUTES;
const {
  contacts: { email, emailUrl, tel, telUrl },
  currentYear
} = CONFIG;

interface FooterProps {
  hasFixedBottomBar?: boolean;
}

export const Footer = ({ hasFixedBottomBar = false }: FooterProps) => {
  const { t } = useTranslation();
  const isMobile = useBelow('tablet');

  const { pages } = useStaticPages();

  return (
    <S.Wrapper data-cy="footer" $hasFixedBottomBar={hasFixedBottomBar}>
      <S.Section>
        <Link data-cy="footer-logo" href={HOME}>
          <LogoIcon width={90} height={20} />
        </Link>

        <S.ContactsWrapper>
          <S.ContactItem as={Link} data-cy="footer-phone" href={telUrl}>
            {tel}
          </S.ContactItem>
          <S.ContactItem as={Link} data-cy="footer-email" href={emailUrl}>
            {email}
          </S.ContactItem>

          <SocialLinks />

          <LanguageSwitcher isAlignedStart={isMobile} />
        </S.ContactsWrapper>
      </S.Section>

      {isMobile && <S.Divider />}

      <S.Section>
        <S.CopyRight data-cy="footer-copyright">
          {t('Footer.copyright', { currentYear })}
        </S.CopyRight>

        <S.LinksWrapper>
          {pages.map(page => (
            <S.FooterLink
              key={page.slug}
              as={Link}
              data-cy={`footer-link-${page.slug}`}
              href={page.href}
              prefetch={false}
            >
              {page.title}
            </S.FooterLink>
          ))}
          <S.FooterLink as={Link} href={ROUTES.SITEMAP} prefetch={false}>
            {t('Footer.nav_link_sitemap')}
          </S.FooterLink>
        </S.LinksWrapper>
      </S.Section>
    </S.Wrapper>
  );
};
